import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { api } from '../lib/api';
import { Button, Input, Select, Badge } from '../atoms';
import { DataTable, StatCard, StatCardRow, useToast } from '../organisms';
import { formatDate } from '../lib/orgTimezone';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * ReportBuilder — /v2/report-builder
 *
 * The self-serve custom report builder. Staff pick a base entity from the Supplier
 * Journey — businesses, people, assessments and their responses, referrals,
 * partners, capabilities, programs, enrollments, sessions, attendance — choose the
 * columns they care about, filter and sort, then run and export (CSV, XLSX or
 * branded HTML). No engineer, no ticket.
 *
 * The catalog is served, not hardcoded here: the entity list and every selectable
 * column come from GET /api/report-builder/catalog (src/services/report-builder.js
 * BASES), so an entity added there appears here with no frontend change.
 *
 * Two panes:
 *   (a) BUILDER — base Select (from /api/report-builder/catalog) → column checklist
 *       → filters editor → optional sort → name + Save/Run. Because running a report
 *       requires a saved definition id, "Run" saves the definition (POST
 *       /api/report-definitions) and immediately runs the returned id
 *       (GET /api/report-builder/:id/run), rendering rows in a DataTable.
 *   (b) SAVED — the org's saved definitions (GET /api/report-builder) with
 *       Run / CSV / Branded actions per row.
 *
 * Backend contract:
 *   GET  /api/report-builder/catalog        → [{ base, label, columns:[{key,label}] }]
 *   GET  /api/report-builder                 → saved defs [{ id, name, base, created_at }]
 *   GET  /api/report-builder/:id/run         → { name, base, columns:[{key,label}], rows, meta:{row_count} }
 *   GET  /api/report-builder/:id/run?format=csv   → CSV download
 *   GET  /api/report-builder/:id/run?format=html  → branded HTML (open in new tab)
 *   POST /api/report-definitions             → { name, base, columns_json, filters_json, sort_json }
 */

interface CatalogColumn {
  key: string;
  label: string;
}

interface CatalogBase {
  base: string;
  label: string;
  columns: CatalogColumn[];
}

interface SavedDefinition {
  id: string;
  name: string;
  base: string;
  created_at?: string;
}

interface RunResult {
  name: string;
  base: string;
  columns: CatalogColumn[];
  rows: Array<Record<string, unknown>>;
  meta?: { row_count: number };
}

type FilterOp = 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains';

interface FilterRow {
  field: string;
  op: FilterOp;
  value: string;
}

interface SortSpec {
  field: string;
  dir: 'asc' | 'desc';
}

const OP_OPTIONS: { value: FilterOp; label: string }[] = [
  { value: 'eq', label: '= equals' },
  { value: 'ne', label: '≠ not equal' },
  { value: 'gt', label: '> greater than' },
  { value: 'lt', label: '< less than' },
  { value: 'gte', label: '≥ at least' },
  { value: 'lte', label: '≤ at most' },
  { value: 'contains', label: 'contains' },
];

// Some CRUD-backed lists come back as a bare array, others wrapped. Normalize both.
function asArray<T>(payload: unknown, key: string): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (payload && typeof payload === 'object') {
    const wrapped = (payload as Record<string, unknown>)[key];
    if (Array.isArray(wrapped)) return wrapped as T[];
  }
  return [];
}

export function ReportBuilder() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [base, setBase] = useState('');
  const [name, setName] = useState('');
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const [sort, setSort] = useState<SortSpec | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  // Save & Run persists a report definition (POST /api/report-definitions) → report.can_add.
  const canAdd = useEntityPermission('report', 'can_add');

  const catalogQuery = useQuery({
    queryKey: ['report-builder-catalog'],
    queryFn: ({ signal }) => api.get<unknown>('/api/report-builder/catalog', signal),
  });
  const catalog = useMemo(() => asArray<CatalogBase>(catalogQuery.data, 'catalog'), [catalogQuery.data]);
  const activeBase = catalog.find(c => c.base === base) ?? null;

  // base key -> human label, so the preview and the saved cards show "Assessment
  // Responses" rather than the raw `assessment_responses` key.
  const baseLabels = useMemo(
    () => new Map(catalog.map(c => [c.base, c.label] as const)),
    [catalog],
  );
  // A saved definition can name a base the catalog no longer serves — that is what
  // happens to every report saved against a retired entity. Only trust the absence
  // of a key once the catalog has actually arrived; while it is loading or errored,
  // `baseLabels` is empty and would condemn every saved report.
  const catalogLoaded = catalogQuery.isSuccess && catalog.length > 0;

  const savedQuery = useQuery({
    queryKey: ['report-builder-saved'],
    queryFn: ({ signal }) => api.get<unknown>('/api/report-builder', signal),
  });
  const savedDefs = useMemo(() => asArray<SavedDefinition>(savedQuery.data, 'definitions'), [savedQuery.data]);

  const runQuery = useQuery<RunResult>({
    queryKey: ['report-builder-run', runId],
    queryFn: ({ signal }) => api.get<RunResult>(`/api/report-builder/${runId}/run`, signal),
    enabled: !!runId,
  });

  const saveMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ id: string }>('/api/report-definitions', body),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['report-builder-saved'] });
      if (created && created.id) {
        setRunId(created.id);
        toast({ variant: 'success', message: 'Report saved — running now.' });
      } else {
        toast({ variant: 'warning', message: 'Report saved, but no id was returned to run.' });
      }
    },
    onError: (err) => {
      toast({ variant: 'error', message: `Save failed: ${(err as Error).message}` });
    },
  });

  function resetBuilderForBase(nextBase: string) {
    setBase(nextBase);
    setSelectedColumns([]);
    setFilters([]);
    setSort(null);
    setRunId(null);
  }

  function toggleColumn(key: string) {
    setSelectedColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
    );
  }

  function addFilter() {
    if (!activeBase || activeBase.columns.length === 0) return;
    setFilters(prev => [...prev, { field: activeBase.columns[0].key, op: 'eq', value: '' }]);
  }

  function updateFilter(index: number, patch: Partial<FilterRow>) {
    setFilters(prev => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  }

  function removeFilter(index: number) {
    setFilters(prev => prev.filter((_, i) => i !== index));
  }

  function buildDefinitionBody(): Record<string, unknown> {
    const cleanFilters = filters
      .filter(f => f.field && f.op)
      .map(f => ({ field: f.field, op: f.op, value: f.value }));
    return {
      name: name.trim(),
      base,
      columns_json: JSON.stringify(selectedColumns),
      filters_json: JSON.stringify(cleanFilters),
      sort_json: JSON.stringify(sort && sort.field ? sort : null),
    };
  }

  function handleSaveAndRun() {
    if (!base) {
      toast({ variant: 'warning', message: 'Pick a base entity first.' });
      return;
    }
    if (!name.trim()) {
      toast({ variant: 'warning', message: 'Give the report a name so you can find it later.' });
      return;
    }
    if (selectedColumns.length === 0) {
      toast({ variant: 'warning', message: 'Select at least one column to include.' });
      return;
    }
    saveMutation.mutate(buildDefinitionBody());
  }

  const columnOptions = (activeBase?.columns ?? []).map(c => ({ value: c.key, label: c.label }));

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>Report builder</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
          maxWidth: 720,
        }}>
          Build your own report: pick an entity from the Supplier Journey — businesses,
          assessments, referrals, partners, programs, enrollments, attendance — choose
          the columns you care about, filter and sort, then run and export as CSV, XLSX
          or a branded PDF-ready page. No engineer, no ticket.
        </p>
      </header>

      {/* ---------- BUILDER ---------- */}
      <Section title="Build a report">
        {catalogQuery.isLoading ? (
          <p style={{ color: 'var(--color-ink-3)', margin: 0 }}>Loading catalog…</p>
        ) : catalog.length === 0 ? (
          <p style={{ color: 'var(--color-muted)', margin: 0 }}>No report bases available.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>
            {/* Base + name */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 'var(--sp-4)' }}>
              <Field label="Base entity">
                <Select
                  options={catalog.map(c => ({ value: c.base, label: c.label }))}
                  placeholder="Choose an entity…"
                  value={base}
                  onChange={e => resetBuilderForBase(e.target.value)}
                />
              </Field>
              <Field label="Report name">
                <Input
                  placeholder="e.g. Procurement-readiness gaps by domain"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </Field>
            </div>

            {activeBase && (
              <>
                {/* Columns */}
                <div>
                  <FieldHeader
                    label={`Columns (${selectedColumns.length} selected)`}
                    action={
                      <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedColumns(activeBase.columns.map(c => c.key))}>
                          Select all
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedColumns([])}>
                          Clear
                        </Button>
                      </div>
                    }
                  />
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
                    gap: 'var(--sp-2)',
                    marginTop: 'var(--sp-2)',
                  }}>
                    {activeBase.columns.map(col => {
                      const checked = selectedColumns.includes(col.key);
                      return (
                        <label
                          key={col.key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--sp-2)',
                            padding: 'var(--sp-2) var(--sp-3)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--r-md)',
                            background: checked ? 'var(--brand-primary-bg)' : 'var(--color-card)',
                            cursor: 'pointer',
                            fontSize: 'var(--fs-sm)',
                            color: 'var(--color-ink)',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleColumn(col.key)}
                            style={{ accentColor: 'var(--brand-primary)' }}
                          />
                          {col.label}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Filters */}
                <div>
                  <FieldHeader
                    label={`Filters (${filters.length})`}
                    action={<Button variant="ghost" size="sm" onClick={addFilter}>+ Add filter</Button>}
                  />
                  {filters.length === 0 ? (
                    <p style={{ margin: 'var(--sp-2) 0 0', color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>
                      No filters — the report returns every row for this entity.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
                      {filters.map((f, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto', gap: 'var(--sp-2)', alignItems: 'center' }}>
                          <Select
                            options={columnOptions}
                            value={f.field}
                            onChange={e => updateFilter(i, { field: e.target.value })}
                          />
                          <Select
                            options={OP_OPTIONS}
                            value={f.op}
                            onChange={e => updateFilter(i, { op: e.target.value as FilterOp })}
                          />
                          <Input
                            placeholder="value"
                            value={f.value}
                            onChange={e => updateFilter(i, { value: e.target.value })}
                          />
                          <Button variant="ghost" size="sm" onClick={() => removeFilter(i)} aria-label="Remove filter">
                            ✕
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Sort */}
                <div>
                  <FieldHeader label="Sort (optional)" />
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 'var(--sp-2)', marginTop: 'var(--sp-2)', maxWidth: 520 }}>
                    <Select
                      options={columnOptions}
                      placeholder="— no sort —"
                      value={sort?.field ?? ''}
                      onChange={e => {
                        const field = e.target.value;
                        setSort(field ? { field, dir: sort?.dir ?? 'asc' } : null);
                      }}
                    />
                    <Select
                      options={[{ value: 'asc', label: 'Ascending' }, { value: 'desc', label: 'Descending' }]}
                      value={sort?.dir ?? 'asc'}
                      disabled={!sort?.field}
                      onChange={e => setSort(prev => (prev ? { ...prev, dir: e.target.value as 'asc' | 'desc' } : prev))}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap', alignItems: 'center' }}>
                  {canAdd && (
                    <Button
                      variant="primary"
                      size="md"
                      loading={saveMutation.isPending}
                      onClick={handleSaveAndRun}
                    >
                      Save &amp; Run
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="md"
                    disabled={!runId}
                    onClick={() => runId && window.open(`/api/report-builder/${runId}/run?format=csv`, '_blank')}
                  >
                    Download CSV
                  </Button>
                  <Button
                    variant="secondary"
                    size="md"
                    disabled={!runId}
                    onClick={() => runId && window.open(`/api/report-builder/${runId}/run?format=xlsx`, '_blank')}
                  >
                    Export XLSX
                  </Button>
                  <Button
                    variant="secondary"
                    size="md"
                    disabled={!runId}
                    onClick={() => runId && window.open(`/api/report-builder/${runId}/run?format=html`, '_blank')}
                  >
                    Open Branded
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Section>

      {/* ---------- PREVIEW ---------- */}
      {runId && (
        <Section title="Preview">
          <RunPreview query={runQuery} baseLabels={baseLabels} />
        </Section>
      )}

      {/* ---------- SAVED ---------- */}
      <Section title="Saved reports">
        {savedQuery.isLoading ? (
          <p style={{ color: 'var(--color-ink-3)', margin: 0 }}>Loading saved reports…</p>
        ) : savedDefs.length === 0 ? (
          <p style={{ color: 'var(--color-muted)', margin: 0 }}>
            No saved reports yet. Build one above and click Save &amp; Run.
          </p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))', gap: 'var(--sp-4)' }}>
            {savedDefs.map(def => (
              <SavedCard
                key={def.id}
                def={def}
                baseLabel={baseLabels.get(def.base)}
                retired={catalogLoaded && !baseLabels.has(def.base)}
                onRun={() => setRunId(def.id)}
                onCsv={() => window.open(`/api/report-builder/${def.id}/run?format=csv`, '_blank')}
                onXlsx={() => window.open(`/api/report-builder/${def.id}/run?format=xlsx`, '_blank')}
                onBranded={() => window.open(`/api/report-builder/${def.id}/run?format=html`, '_blank')}
              />
            ))}
          </div>
        )}
      </Section>
    </>
  );
}

function RunPreview({
  query, baseLabels,
}: {
  query: ReturnType<typeof useQuery<RunResult>>;
  baseLabels: Map<string, string>;
}) {
  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
    if (!query.data) return [];
    return query.data.columns.map(col => ({
      accessorKey: col.key,
      header: col.label,
      cell: ({ getValue }) => formatCell(getValue()),
    }));
  }, [query.data]);

  if (query.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)', margin: 0 }}>Running report…</p>;
  }
  if (query.error) {
    return (
      <div style={{
        padding: 'var(--sp-4)',
        background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)',
        color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        Failed to run report: {(query.error as Error).message}
      </div>
    );
  }
  if (!query.data) return null;

  const result = query.data;
  const rowCount = result.meta?.row_count ?? result.rows.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <StatCardRow>
        <StatCard label="Rows" value={String(rowCount)} caption="returned by query" accent="hunter" />
        <StatCard label="Entity" value={baseLabels.get(result.base) ?? result.base} caption="entity queried" accent="info" />
        <StatCard label="Columns" value={String(result.columns.length)} caption="in result schema" accent="green" />
      </StatCardRow>
      {result.rows.length === 0 ? (
        <div style={{
          padding: 'var(--sp-7)',
          background: 'var(--color-card)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)',
          color: 'var(--color-muted)',
          textAlign: 'center',
        }}>
          No rows matched. Loosen the filters and run again.
        </div>
      ) : (
        <DataTable data={result.rows} columns={columns} />
      )}
    </div>
  );
}

function SavedCard({
  def, baseLabel, retired, onRun, onCsv, onXlsx, onBranded,
}: {
  def: SavedDefinition;
  /** Human label from the catalog; undefined when the catalog no longer serves this base. */
  baseLabel?: string;
  /** True only once the catalog has loaded AND it does not contain this base. */
  retired: boolean;
  onRun: () => void;
  onCsv: () => void;
  onXlsx: () => void;
  onBranded: () => void;
}) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--sp-3)',
    }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 'var(--fs-md)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
            {def.name}
          </h3>
          <Badge variant={retired ? 'warning' : 'info'}>{baseLabel ?? def.base}</Badge>
        </div>
        {def.created_at && (
          <div style={{ marginTop: 'var(--sp-2)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>
            saved {formatDate(def.created_at, { dateOnly: true })}
          </div>
        )}
        {retired && (
          <div style={{ marginTop: 'var(--sp-2)', fontSize: 'var(--fs-sm)', color: 'var(--color-status-amber)' }}>
            Built on “{def.base}”, an entity this system no longer holds. The report
            can’t be run — rebuild it above on a current entity.
          </div>
        )}
      </div>
      {/* A retired base makes every action a guaranteed error (the server rejects an
          unknown base), so the buttons are disabled rather than left to fail. */}
      <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'auto', flexWrap: 'wrap' }}>
        <Button variant="primary" size="sm" disabled={retired} onClick={onRun}>Run</Button>
        <Button variant="secondary" size="sm" disabled={retired} onClick={onCsv}>CSV ↓</Button>
        <Button variant="secondary" size="sm" disabled={retired} onClick={onXlsx}>XLSX ↓</Button>
        <Button variant="secondary" size="sm" disabled={retired} onClick={onBranded}>Branded</Button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 'var(--sp-6)' }}>
      <h2 style={{
        margin: '0 0 var(--sp-3)',
        fontSize: 'var(--fs-xl)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--color-ink)',
      }}>{title}</h2>
      <div style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-5)',
      }}>
        {children}
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1-5)' }}>
      <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-3)' }}>{label}</span>
      {children}
    </div>
  );
}

function FieldHeader({ label, action }: { label: string; action?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
      <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-3)' }}>{label}</span>
      {action}
    </div>
  );
}

function formatCell(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string' && value.length > 80) return value.slice(0, 80) + '…';
  return String(value);
}
